import {
    HubConnection,
    HubConnectionBuilder,
    LogLevel,
    HttpTransportType,
    IHttpConnectionOptions,
} from "@aspnet/signalr";
import { app, alarm, enrollState, sensor } from "@/store";
import {
    AlarmStatus,
    ILocationFromParticle,
    IMiscInfo,
    IStatusChange,
    mapJsonToAlarmStatus,
} from "@/models/AlarmStatus";
import { configureOidc } from "@/store/services/security.service";
import { unpairUi } from "@/store/unpairUi";
import { Log } from "./Logging";

let connectionObj: HubConnection | null = null;
let lastSensorsFetch = 0;
let pendingSensorRefreshId: number | null = null;
let isStoppingConnection = false;
const SENSOR_REFRESH_MIN_MS = 5000; // refresh at most once per 5s
// The device row is deleted asynchronously by the toapp queue, so poll after LEAVE.
const DEVICE_LEAVE_REFRESH_DELAYS_MS = [0, 2000, 5000, 10000, 15000];

function parseEnrollingDeviceAddresses(value?: string) {
    if (!value) {
        console.warn("SignalR: DeviceAdding payload is missing.");
        return null;
    }

    let payload: unknown;
    try {
        payload = JSON.parse(value);
    } catch (error) {
        console.warn("SignalR: DeviceAdding payload is not valid JSON.", error);
        return null;
    }

    if (typeof payload !== "object" || payload === null) {
        console.warn("SignalR: DeviceAdding payload must be an object.");
        return null;
    }

    const deviceInfo = payload as { device?: unknown; shortAddress?: unknown };
    if (typeof deviceInfo.device !== "string" || !deviceInfo.device.trim() ||
        typeof deviceInfo.shortAddress !== "string" || !deviceInfo.shortAddress.trim()) {
        console.warn("SignalR: DeviceAdding payload is missing device addresses.");
        return null;
    }

    return {
        longAddress: deviceInfo.device.trim().toUpperCase(),
        shortAddress: deviceInfo.shortAddress.trim().toUpperCase(),
    };
}

export function handleStatusChange(value: string, onAlarm = false) {
    if (value === "") {
        console.warn("Status value is empty, returning early.");
        return;
    }

    const statusChange: IStatusChange = JSON.parse(value);

    // Always apply age first (also clears per-device spinner once age != -1)
    sensor.SetSensorAge(statusChange);

    const senderDevice = sensor.devices?.find(
        (i) => i.attachedDeviceId === statusChange.device
    );

    // Status is the raw IAS Zone Status hex value (e.g. "0020","0021","0034","0035"),
    // not a plain 0/1 suffix - decode it as a bitmask instead of comparing string endings.
    // bit0=Alarm1(open/on), bit2=Tamper. Other bits (battery/reporting/etc.) are ignored here.
    const statusValue = parseInt(statusChange.status, 16);
    const hasValidStatus = statusChange.status.length > 0 && !Number.isNaN(statusValue);
    const isAlarm1 = hasValidStatus && (statusValue & 0x1) === 1;
    const isTampered = hasValidStatus && (statusValue & 0x4) === 0x4;

    if (onAlarm) {
        if (senderDevice) {
            alarm.setAlarmOrigin(senderDevice.attachedDeviceTypeId);
        }
        // Alarm1 bit always wins; otherwise fall back to age-based reason.
        let isAged = statusChange.age && Number(statusChange.age) > 0;
        alarm.setAlarmReason(isAlarm1 ? "alarm" : isAged ? "age" : "");
        alarm.setAlarmModeTriggered(true);

        // Start/refresh 240s silence window on alarm events
        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    }

    if (hasValidStatus) {
        const typeDescription = ((senderDevice as any)?.deviceTypeDescription || "").toString().toLowerCase();
        const isDoorOrHatch = typeDescription.includes("door") || typeDescription.includes("hatch") || typeDescription.includes("contact");
        const pairingTypeIdRaw = enrollState.EnrolledDeviceTypeId ?? enrollState.lastEnrolledDeviceTypeId;
        const pairingTypeId = pairingTypeIdRaw ? Number(pairingTypeIdRaw) : null;
        const pairingMatches =
            senderDevice &&
            pairingTypeId !== null &&
            !Number.isNaN(pairingTypeId) &&
            senderDevice.attachedDeviceTypeId === pairingTypeId;
        const hasPairingContext = pairingTypeId !== null && !Number.isNaN(pairingTypeId);
        const pairedAt = (senderDevice as any)?.pairedAt as number | undefined;
        const enrollAt = enrollState.lastEnrollAt ?? pairedAt;
        const recentAt = Math.max(enrollAt ?? 0, pairedAt ?? 0) || undefined;
        const DOOR_PAIRING_UNKNOWN_MS = 60 * 1000;
        const inPairingWindow = recentAt !== undefined && Date.now() - recentAt < DOOR_PAIRING_UNKNOWN_MS;
        const allowUnknown = inPairingWindow && (isDoorOrHatch || pairingMatches || (!senderDevice && hasPairingContext));
        const isInitialUnknown =
            statusValue === 0 &&
            allowUnknown;
        const nextStatus = isAlarm1 ? true : isInitialUnknown ? null : false;

        Log.information(
            `StatusChange debug: dev=${statusChange.device} status=${statusChange.status} next=${nextStatus} tampered=${isTampered} ` +
            `pairType=${pairingTypeIdRaw ?? ""} devTypeId=${(senderDevice as any)?.attachedDeviceTypeId ?? ""} ` +
            `typeDesc=${typeDescription} enrollAt=${enrollAt ?? ""} pairedAt=${pairedAt ?? ""} ` +
            `inWindow=${inPairingWindow} allowUnknown=${allowUnknown} pairingMatches=${pairingMatches}`
        );

        sensor.SetSensorStatus({
            device: statusChange.device,
            status: nextStatus,
            tampered: isTampered,
        });
    } else {
        console.warn(
            `Unhandled status ${statusChange.status} for device ${statusChange.device}`
        );
    }

    refreshSensorsThrottled();
}

export async function configureMessageHub() {
    isStoppingConnection = false;

    if (connectionObj) return Promise.resolve(connectionObj);

    const mainOidc = await configureOidc();
    const options: IHttpConnectionOptions = {
        accessTokenFactory: getAccessToken,
        skipNegotiation: true,
        transport: HttpTransportType.WebSockets,
    };

    const connection = new HubConnectionBuilder()
        .withUrl(`${mainOidc.appUrl}hubs/message`, options)
        .configureLogging(LogLevel.Debug)
        .build();

    // Simple toasts
    connection.on("SendNotificationToClient", (value) => {
        app.error = value;
    });

    // Arm/disarm
    connection.on("SetArmOrDisarmOnClient", (value) => {
        alarm.setArmed(value);
    });

    // Siren off
    connection.on("SetSirenOnClient", (value) => {
        alarm.setSiren(value);
    });

    // Alarm status payloads (keep location/voltage parsing if you need it later)
    connection.on("SetStatusOnClient", (value: string) => {
        if (value !== "") {
            const status: AlarmStatus = mapJsonToAlarmStatus(value);
            const i1 = parseFloat(
                status.VSupplies && status.VSupplies[0] && status.VSupplies[0].I1
                    ? status.VSupplies[0].I1
                    : "-999"
            );
            const v4 = parseFloat(
                status.VSupplies && status.VSupplies[0] && status.VSupplies[0].V4
                    ? status.VSupplies[0].V4
                    : "-999"
            );

            refreshSensorsThrottled();

            alarm.setPumpRunningState(i1 >= 0.75); // TODO: Fetch threshold from user settings once implemented
            // Connected if I1 is not -999 and a valid value
            alarm.setCurrentSensorConnected(i1 !== -999 && !isNaN(i1));

            // Set true only if V4 is a valid number and greater than 3
            alarm.setGasAlarmState(v4 > 3);
            // Connected if V4 is not -999 and a valid value
            alarm.setGasSensorConnected(v4 !== -999 && !isNaN(v4));

            status.fetchAt = new Date();
            alarm.setStatus(status);
        }
    });

    // Enrollment flow
    connection.on("JoinOpen", () => {
        Log.information(`SignalR: StartEnroll received`);
        enrollState.joinOpen();
    });
    connection.on("DeviceAdding", (value?: string) => {
        Log.information(`SignalR: DeviceAdding received`);
        enrollState.startConfiguring(parseEnrollingDeviceAddresses(value));
    });
    connection.on("DeviceAdded", () => {
        Log.information("SignalR: DeviceAdded received, completing enrollment.");
        enrollState.success();
        refreshSensorsThrottled();
        // Battery data can lag behind pairing; do one delayed refresh to pick it up.
        refreshSensorsDelayed(4000);
    });
    connection.on("JoinClosed", () => {
        Log.information("SignalR: JoinClosed received, handling closed enrollment.");
        enrollState.handleJoinClosed();
    });
    connection.on("JoinFailed", () => {
        Log.information("SignalR: EnrollFailed received, failing enrollment.");
        enrollState.fail();
    });

    // Unpair/leave events -> refresh device list
    const handleDeviceLeave = (value?: string) => {
        Log.information(`SignalR: DeviceLeave received${value ? ` (${value})` : ""}`);
        unpairUi.complete(value);
        DEVICE_LEAVE_REFRESH_DELAYS_MS.forEach(delay => {
            if (delay === 0) refreshSensorsNow();
            else window.setTimeout(() => refreshSensorsNow(), delay);
        });
    };
    connection.on("DeviceLeft", () => {
        Log.information("SignalR: DeviceLeft received, refreshing sensors.");
        handleDeviceLeave();
    });

    // Position & misc
    connection.on("UpdatePosition", (value: string) => {
        if (value !== "") {
            const location: ILocationFromParticle = JSON.parse(value);
            alarm.setCurrentLocation(location);
        }
    });
    connection.on("UpdateMiscInfo", (value: string) => {
        if (value !== "") {
            const miscInfo: IMiscInfo = JSON.parse(value);
            alarm.setCurrentMiscInfo(miscInfo);
        }
    });

    connection.on("UpdateStatusChange", handleStatusChange);
    connection.on("UpdateStatusChangeOnAlarm", (value: string) =>
        handleStatusChange(value, true)
    );
    connection.on("UpdateAgingDev", handleStatusChange);

    // Toasts
    connection.on("ShowMessage", (msg: string) => {
        if (msg !== "") app.success = msg;
    });
    connection.on("ShowError", (msg: string) => {
        if (msg !== "") app.error = msg;
    });
    connection.on("GeofenceBreached", () => {
        if (!alarm.armed) {
            return;
        }

        alarm.setAlarmOrigin(99);
        alarm.setAlarmReason("alarm");
        alarm.setAlarmModeTriggered(true);

        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    });

    connection.on("TamperTriggered", () => {
        if (!alarm.armed) {
            return;
        }

        alarm.setAlarmOrigin(18);
        alarm.setAlarmReason("alarm");
        alarm.setAlarmModeTriggered(true);

        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    });

    connection.on("BilgeTriggered", () => {
        if (!alarm.armed) {
            return;
        }

        alarm.setAlarmOrigin(17);
        alarm.setAlarmReason("alarm");
        alarm.setAlarmModeTriggered(true);

        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    });

    connection.on("GasTriggered", () => {
        if (!alarm.armed) {
            return;
        }

        alarm.setAlarmOrigin(7);
        alarm.setAlarmReason("alarm");
        alarm.setAlarmModeTriggered(true);

        alarm.startSilenceWindow(alarm.sirenDuration);
        alarm.setSecondsSinceLastAlarm(0);
    });

    connection.onclose(() => {
        if (!isStoppingConnection) {
            startConnection(connection);
        }
    });
    connectionObj = connection;
    await startConnection(connectionObj);
    return connectionObj;
}

export async function stopMessageHub() {
    isStoppingConnection = true;

    if (!connectionObj) {
        return;
    }

    const connection = connectionObj;
    connectionObj = null;

    try {
        await connection.stop();
    } catch (err) {
        Log.error("Failed to stop hub connection", err);
    }
}

const getAccessToken = async (): Promise<string> => {
    const mainOidc = await configureOidc();
    return mainOidc.accessToken;
};

const startConnection = async (connection: HubConnection) => {
    if (connection) {
        const startedPromise: Promise<void> = connection
            .start()
            .catch((err) => {
                Log.error("Failed to connect with hub", err);
                return new Promise((resolve, reject) =>
                    setTimeout(
                        () =>
                            startConnection(connection)
                                .then(() => resolve())
                                .catch(reject),
                        5000
                    )
                );
            });
        return startedPromise;
    }
};

function refreshSensorsNow() {
    if (pendingSensorRefreshId !== null) {
        clearTimeout(pendingSensorRefreshId);
        pendingSensorRefreshId = null;
    }
    lastSensorsFetch = Date.now();
    sensor.GetData();
}

function refreshSensorsThrottled() {
    const elapsed = Date.now() - lastSensorsFetch;
    if (elapsed >= SENSOR_REFRESH_MIN_MS) {
        refreshSensorsNow();
        return;
    }
    // Defer instead of dropping, otherwise the last update is never applied.
    if (pendingSensorRefreshId !== null) return;
    pendingSensorRefreshId = window.setTimeout(() => {
        pendingSensorRefreshId = null;
        refreshSensorsNow();
    }, SENSOR_REFRESH_MIN_MS - elapsed);
}

function refreshSensorsDelayed(delayMs: number) {
    window.setTimeout(() => refreshSensorsThrottled(), delayMs);
}